import { AsyncPipe } from '@angular/common';
import { Component, computed, inject, input, viewChild } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { SiteWorkflowDirective } from '@features/feature-workflow/directives/site-workflow.directive';

import {
  injectFetchDocument,
  injectRemoveDocumentMutation,
} from '@models/documents/api/documents.api';
import {
  injectProcessSiteDocumentEventMutation,
  injectUploadSiteBudgetDocumentsMutation,
} from '@models/sites/api/site-documents.api';

import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { File } from '@shared/components/file-manager/state/file.model';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SkeletonLoaderDirective } from '@shared/directives/skeleton-loader.directive';
import { FileMetadata } from '@shared/services/api.service';
import { DocumentType, EntityType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Maybe } from '@shared/utils/utils';

@Component({
  hostDirectives: [SiteWorkflowDirective],
  imports: [
    FileManagerComponent,
    AsyncPipe,
    IconComponent,
    ErrorsTooltipComponent,
    SkeletonLoaderDirective,
  ],
  selector: 'aux-site-budget-supporting-documents',
  styleUrls: ['./site-budget-supporting-documents.component.scss'],
  templateUrl: './site-budget-supporting-documents.component.html',
})
export class SiteBudgetSupportingDocumentsComponent {
  siteWorkflow = inject(SiteWorkflowDirective);

  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);

  selectedSiteBudgetVersion = input.required<null | string>();
  selectedSiteId = input.required<Maybe<string>>();

  siteBudgetPath = computed(() => {
    return `trials/${this.mainQuery.trialKey()}/sites/${this.selectedSiteId()}/${this.selectedSiteBudgetVersion()}/contracts/`;
  });

  siteBudgetManager = viewChild<FileManagerComponent>('siteBudgetManager');

  fetchSiteBudgetDocumentsQuery = injectFetchDocument(
    this.siteBudgetManager,
    this.siteBudgetPath,
    this.selectedSiteBudgetVersion
  );

  sitePath = computed(() => {
    return `trials/${this.mainQuery.trialKey()}/sites/${this.selectedSiteId()}/contracts/`;
  });

  siteManager = viewChild<FileManagerComponent>('siteManager');

  fetchSiteDocumentsQuery = injectFetchDocument(
    this.siteManager,
    this.sitePath,
    this.selectedSiteBudgetVersion
  );

  private processSiteDocumentEventMutation = injectProcessSiteDocumentEventMutation();
  private removeSiteBudgetDocumentMutation = injectRemoveDocumentMutation(this.siteBudgetManager);
  private removeSiteDocumentMutation = injectRemoveDocumentMutation(this.siteManager);
  private uploadSiteBudgetDocumentsMutation = injectUploadSiteBudgetDocumentsMutation(
    this.siteBudgetManager
  );

  isInitialSiteBudget = input(false);

  isProcessing = computed(
    () =>
      this.uploadSiteBudgetDocumentsMutation.isPending() ||
      this.processSiteDocumentEventMutation.isPending() ||
      this.removeSiteBudgetDocumentMutation.isPending() ||
      this.removeSiteDocumentMutation.isPending() ||
      this.fetchSiteBudgetDocumentsQuery.isFetching() ||
      (this.isInitialSiteBudget() && this.fetchSiteDocumentsQuery.isFetching())
  );
  siteBudgetDocumentsMetadata = computed(
    () =>
      ({
        documentType: DocumentType.DOCUMENT_SITE_AGREEMENTS,
        entity_id: this.selectedSiteBudgetVersion(),
        entity_type_id: EntityType.SITE_BUDGET_VERSION,
        site: this.selectedSiteId(),
      }) as FileMetadata
  );
  siteDocumentsMetadata = computed(
    () =>
      ({
        documentType: DocumentType.DOCUMENT_SITE_AGREEMENTS,
        entity_id: this.selectedSiteId(),
        entity_type_id: EntityType.SITE,
        site: this.selectedSiteId(),
      }) as FileMetadata
  );

  uploadedSiteBudgetFiles$: Observable<File[]> = of([]);
  uploadedSiteFiles$: Observable<File[]> = of([]);

  deleteFile(file: File & { source?: string }) {
    if (this.isProcessing() || this.siteWorkflow.disabledUpdateSiteCostUI()) return;

    if (file.source === 'site') {
      void this.removeSiteDocumentMutation.mutateAsync(file);
    } else {
      void this.removeSiteBudgetDocumentMutation.mutateAsync(file);
    }
  }

  downloadFile(file: File & { source?: string }) {
    const manager = file.source === 'site' ? this.siteManager() : this.siteBudgetManager();

    if (file) {
      if (!file.rawFile) {
        const fileSuccess = manager?.apiService.downloadFileFromPath(
          file.bucket_key,
          file.fileName
        );

        if (!fileSuccess) {
          this.overlayService.error('There was a problem downloading the file!');
        }
      } else {
        const newBlob = new Blob([file.rawFile]);

        manager?.apiService.downloadBlob(newBlob, file.fileName);
      }
    } else {
      this.overlayService.error('There was a problem downloading the file!');
    }
  }

  get mergedFiles$(): Observable<(File & { source: string })[]> {
    return combineLatest([
      this.uploadedSiteBudgetFiles$ ?? of([]),
      this.uploadedSiteFiles$ ?? of([]),
    ]).pipe(
      map(([files1, files2]) => {
        const taggedFiles1 = files1.map((f) => ({ ...f, source: 'siteBudget' }));
        const taggedFiles2 = this.isInitialSiteBudget()
          ? files2.map((f) => ({ ...f, source: 'site' }))
          : [];

        return [...taggedFiles1, ...taggedFiles2].sort((a, b) =>
          a.fileName.localeCompare(b.fileName)
        );
      })
    );
  }

  async onFilesAdded() {
    if (this.isProcessing()) return;

    const newFiles =
      this.siteBudgetManager()?.fileQuery.getAll({
        filterBy: (entity) => !entity.uploaded && !!entity.rawFile,
      }) || [];

    await this.uploadSiteBudgetDocumentsMutation.mutateAsync({
      siteBudgetVersionId: this.selectedSiteBudgetVersion()!,
      siteId: this.selectedSiteId()!,
    });

    if (newFiles.length > 0) {
      await this.processSiteDocumentEventMutation.mutateAsync({
        files: newFiles,
        message: '',
        siteBudgetVersionId: this.selectedSiteBudgetVersion()!,
        siteId: this.selectedSiteId()!,
      });
    }

    void this.fetchSiteBudgetDocumentsQuery.refetch();
  }

  siteBudgetManagerReady(fileManager: FileManagerComponent) {
    this.uploadedSiteBudgetFiles$ = fileManager.fileQuery.selectAll({ sortBy: 'create_date' });
  }

  siteManagerReady(fileManager: FileManagerComponent) {
    this.uploadedSiteFiles$ = fileManager.fileQuery.selectAll({ sortBy: 'create_date' });
  }
}
